
import { useState, useEffect } from "react";
import { format, differenceInMonths } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AnimatedPage from "@/components/AnimatedPage";
import { Plus, Search, Edit, Trash2, Calendar, IndianRupee } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getLoans, deleteLoan } from "@/lib/api";
import { Loan } from "@/lib/types";
import NavHeader from "@/components/NavHeader";

const Loans = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [loans, setLoans] = useState<Loan[]>([]);
  const [filteredLoans, setFilteredLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loanToDelete, setLoanToDelete] = useState<Loan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const loanData = await getLoans();
        setLoans(loanData);
        setFilteredLoans(loanData);
      } catch (error) {
        console.error("Error fetching loans:", error);
        toast.error("Failed to load loans.");
        setLoans([]);
        setFilteredLoans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLoans();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredLoans(loans);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = loans.filter(
        (loan) => loan.customer_name.toLowerCase().includes(query)
      );
      setFilteredLoans(filtered);
    }
  }, [searchQuery, loans]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleDeleteClick = (loan: Loan) => {
    setLoanToDelete(loan);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!loanToDelete) return;

    try {
      await deleteLoan(loanToDelete.id);
      setLoans(loans.filter((loan) => loan.id !== loanToDelete.id));
      toast.success("Loan deleted successfully");
    } catch (error) {
      console.error("Error deleting loan:", error);
      toast.error("Failed to delete loan");
    } finally {
      setLoanToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // Calculate months completed for a loan
  const calculateMonthsCompleted = (loanStartDate: string) => {
    const startDate = new Date(loanStartDate);
    const currentDate = new Date();
    const monthsCompleted = differenceInMonths(currentDate, startDate);
    return Math.max(0, monthsCompleted);
  };

  if (loading) {
    return (
      <AnimatedPage>
        <div className="container max-w-6xl py-8 flex justify-center items-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading loans...</p>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <div className="container max-w-6xl py-8 space-y-8">
        <NavHeader title="Loans" subtitle="Manage your loans and interest payments" />
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search loans..."
              className="pl-10 bg-background"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button onClick={() => navigate("/loans/new")} className="whitespace-nowrap">
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">New Loan</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>

        {filteredLoans.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 space-y-4"
          >
            <h3 className="text-lg font-medium">No loans found</h3>
            <p className="text-muted-foreground">
              {loans.length === 0
                ? "Get started by creating your first loan"
                : "No loans match your search criteria"}
            </p>
            {loans.length === 0 && (
              <Button
                onClick={() => navigate("/loans/new")}
                className="mt-4"
                variant="outline"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Loan
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="hidden sm:table-cell">Amount</TableHead>
                  <TableHead className="hidden lg:table-cell">Interest Rate</TableHead>
                  <TableHead className="hidden md:table-cell">Months</TableHead>
                  <TableHead>Interest</TableHead>
                  <TableHead className="hidden lg:table-cell">Next Due Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLoans.map((loan) => {
                  const monthsCompleted = calculateMonthsCompleted(loan.firstDueDate);
                  
                  return (
                    <TableRow key={loan.id}>
                      <TableCell className="font-medium">
                        <div>
                          <div>{loan.customer_name}</div>
                          <div className="sm:hidden text-sm text-muted-foreground">
                            {formatCurrency(loan.amount)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{formatCurrency(loan.amount)}</TableCell>
                      <TableCell className="hidden lg:table-cell">{loan.interest_rate}%</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="font-medium">{monthsCompleted}</span>
                        <span className="text-muted-foreground">/{loan.tenure}</span>
                      </TableCell>
                      <TableCell>{formatCurrency(loan.monthlyInterest)}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {format(new Date(loan.firstDueDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/loans/${loan.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(loan)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the loan for{" "}
              <span className="font-medium">{loanToDelete?.customer_name}</span> and all
              associated reminders. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AnimatedPage>
  );
};

export default Loans;
